import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Pinned below the scrollable body (e.g. auth) so menus are not clipped. */
  footer?: ReactNode;
}

const DRAWER_ID = "gf-mobile-nav-drawer";
/** Keep in sync with `@media (max-width: 760px)` in globals.css / Redoc shell. */
const MOBILE_NAV_MEDIA = "(max-width: 760px)";

export function MobileNavDrawer({
  open,
  onClose,
  children,
  footer,
}: MobileNavDrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();

  useDialogFocusTrap(open, panelRef, onClose, closeBtnRef);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // DevTools / rotation can leave the drawer open past the mobile breakpoint.
  useEffect(() => {
    if (!open) {
      return;
    }
    const media = window.matchMedia(MOBILE_NAV_MEDIA);
    const closeIfDesktop = () => {
      if (!media.matches) {
        onClose();
      }
    };
    closeIfDesktop();
    media.addEventListener("change", closeIfDesktop);
    return () => {
      media.removeEventListener("change", closeIfDesktop);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="mobile-nav-drawer" data-testid="gf-mobile-nav-drawer">
      <button
        type="button"
        className="mobile-nav-drawer__backdrop"
        aria-label="Close menu"
        data-testid="gf-mobile-nav-backdrop"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        id={DRAWER_ID}
        className="mobile-nav-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="gf-mobile-nav-panel"
      >
        <div className="mobile-nav-drawer__header">
          <h2 id={titleId} className="mobile-nav-drawer__title">
            Menu
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="mobile-nav-drawer__close"
            aria-label="Close menu"
            data-testid="gf-mobile-nav-close"
            onClick={onClose}
          >
            <i className="ti ti-x" aria-hidden />
          </button>
        </div>
        <div className="mobile-nav-drawer__body">{children}</div>
        {footer ? (
          <div className="mobile-nav-drawer__footer">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export { DRAWER_ID as MOBILE_NAV_DRAWER_ID };
